<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { bindTutorPhone, sendVerificationCode, remindMeLater } from "@/api/teachinfo";
import { useUserStore } from "@/stores/user";
import { useConfirm, useSnackbar } from "@/composables/useFeedback";

const userStore = useUserStore();
const { alert } = useConfirm();
const snackbar = useSnackbar();

const dialog = ref(false);
const phoneNumber = ref("");
const verificationCode = ref("");
const areaCode = ref("63");
const canGet = ref(true);
const getCodeBtn = ref("Send");
const phoneError = ref("");
const codeError = ref("");
let timer = null;
let count = 60;

function clearTimer() {
  if (timer) clearInterval(timer);
  timer = null;
  canGet.value = true;
  getCodeBtn.value = "Send";
}

watch(dialog, (val) => {
  if (!val) clearTimer();
});

watch(
  () => userStore.isNeedUpdatePhone,
  (need) => {
    if (need) dialog.value = true;
  }
);

onMounted(() => {
  if (userStore.isNeedUpdatePhone) dialog.value = true;
});

onUnmounted(() => clearTimer());

function onlyDigits(e, target) {
  const v = String(e).replace(/[^0-9]+/g, "");
  if (target === "phone") phoneNumber.value = v;
  else verificationCode.value = v;
}

async function sendCode() {
  verificationCode.value = "";
  codeError.value = "";
  if (!phoneNumber.value) {
    phoneError.value = "Required.";
    return;
  }
  phoneError.value = "";
  try {
    const res = await sendVerificationCode(phoneNumber.value, `+${areaCode.value}`);
    const resInfo = res?.data || {};
    if (!timer && resInfo.ErrorCode == 0) {
      count = 60;
      canGet.value = false;
      getCodeBtn.value = `(${count})s`;
      timer = setInterval(() => {
        if (count > 0) {
          count -= 1;
          getCodeBtn.value = `(${count})s`;
        } else {
          clearTimer();
        }
      }, 1000);
    }
    snackbar.show(resInfo.Msg || (resInfo.ErrorCode === 0 ? "Sent" : "Failed"), {
      type: resInfo.ErrorCode === 0 ? "success" : "error",
    });
  } catch (_) {
    snackbar.show("Failed to send code", { type: "error" });
  }
}

async function bindPhone() {
  phoneError.value = phoneNumber.value ? "" : "Required.";
  codeError.value = verificationCode.value ? "" : "Required.";
  if (!phoneNumber.value || !verificationCode.value) return;

  try {
    const res = await bindTutorPhone(
      phoneNumber.value,
      verificationCode.value,
      `+${areaCode.value}`
    );
    const resInfo = res?.data || {};
    if (resInfo.ErrorCode === 2) {
      await alert("Verification code input error", { type: "error" });
      return;
    }
    if (resInfo.ErrorCode === 0) {
      dialog.value = false;
      userStore.isNeedUpdatePhone = false;
      await alert("Binding success", { type: "success" });
      return;
    }
    await alert(resInfo.Msg || "Binding failed", { type: "error" });
  } catch (_) {
    await alert("Binding failed", { type: "error" });
  }
}

function remindLater() {
  remindMeLater().catch(() => {});
  dialog.value = false;
}
</script>

<template>
  <el-dialog
    v-model="dialog"
    width="550px"
    align-center
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    class="binding-phone-dialog"
  >
    <h3 class="bp-title">Binding Mobile Phone</h3>

    <div class="bp-field">
      <label class="bp-label">Phone Number</label>
      <div class="bp-phone-row">
        <span class="bp-area">
          +&nbsp;<input v-model="areaCode" class="area-input" type="text" />
        </span>
        <el-input
          :model-value="phoneNumber"
          placeholder=""
          @update:model-value="(v) => onlyDigits(v, 'phone')"
        />
      </div>
      <p v-if="phoneError" class="bp-error">{{ phoneError }}</p>
    </div>

    <div class="bp-field">
      <label class="bp-label">Verification Code</label>
      <div class="bp-code-row">
        <el-input
          :model-value="verificationCode"
          @update:model-value="(v) => onlyDigits(v, 'code')"
        />
        <button type="button" class="tutor-blue-btn send-btn" :disabled="!canGet" @click="sendCode">
          {{ getCodeBtn }}
        </button>
      </div>
      <p v-if="codeError" class="bp-error">{{ codeError }}</p>
    </div>

    <p class="bp-hint">* Please make sure it is your contact information</p>

    <div class="bp-footer">
      <button type="button" class="tutor-blue-btn" @click="remindLater">Remind in 6 hours</button>
      <button type="button" class="tutor-red-btn" @click="bindPhone">Submit</button>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.bp-title {
  margin: 0 0 16px;
  color: #32397a;
  font-size: 18px;
  font-weight: 700;
}

.bp-field {
  margin-bottom: 12px;
}

.bp-label {
  display: block;
  margin-bottom: 6px;
  color: #32397a;
  font-size: 14px;
}

.bp-phone-row {
  display: flex;
  gap: 10px;
  align-items: center;
}

.bp-area {
  display: flex;
  align-items: center;
  min-width: 72px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid rgba(50, 57, 122, 0.4);
  border-radius: 4px;
  color: #32397a;
  font-weight: 700;
  box-sizing: border-box;
}

.area-input {
  width: 36px;
  border: none;
  outline: none;
  background: transparent;
  color: #32397a;
  font-weight: 700;
}

.bp-code-row {
  display: flex;
  gap: 10px;
  align-items: center;

  .el-input {
    flex: 1;
  }

  .send-btn {
    min-width: 88px;
    height: 32px !important;
  }
}

.bp-hint {
  margin: 8px 0 0;
  color: rgba(50, 57, 122, 0.4);
  font-size: 13px;
}

.bp-error {
  margin: 4px 0 0;
  color: #ee344c;
  font-size: 12px;
}

.bp-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 28px;
}
</style>

<style lang="scss">
.binding-phone-dialog .el-dialog__header {
  display: none;
}

.binding-phone-dialog .el-dialog__body {
  padding: 20px 24px 24px;
}
</style>
